import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '../api/problemCode';
import { PreparedMaterial } from './extraction/extractionTypes';
import { useLazyGetExtractionPageQuery } from './jobEndpoints';

export interface PagePreviewProps {
  material: PreparedMaterial;
  defaultPage: number;
}

/**
 * Shows one page's kept text of a prepared PDF (QZ-D82): read locally when this browser just
 * extracted it, otherwise fetched from the API, the only time a hit's text is fetched (QZ-D83).
 */
export const PagePreview = ({ material, defaultPage }: PagePreviewProps) => {
  const { t } = useTranslation();
  const [getPage, { isFetching }] = useLazyGetExtractionPageQuery();
  const [pageInput, setPageInput] = useState(String(defaultPage));
  const [shown, setShown] = useState<{ page: number; text: string } | undefined>();
  const [error, setError] = useState<string | undefined>();
  // Bumped on each Show so a slower earlier fetch cannot overwrite a later one.
  const showIdRef = useRef(0);

  const show = async () => {
    const typed = Number(pageInput.trim());
    if (pageInput.trim() === '' || !Number.isFinite(typed)) {
      return;
    }
    // Out-of-range numbers are clamped to the PDF's pages, and the input shows the page read.
    const page = Math.min(Math.max(Math.round(typed), 1), material.pdfPageCount);
    setPageInput(String(page));
    const showId = ++showIdRef.current;
    setError(undefined);

    if (material.text) {
      const text = material.text.pages.find((p) => p.page === page)?.text ?? '';
      setShown({ page, text });
      return;
    }

    setShown(undefined);
    try {
      const response = await getPage({ id: material.extractionId, page }, true).unwrap();
      if (showIdRef.current === showId) {
        setShown({ page, text: response.text });
      }
    } catch (thrown) {
      if (showIdRef.current === showId) {
        setError(
          t(
            problemCode(thrown) === 'ExtractionNotFound'
              ? 'error.ExtractionNotFound'
              : 'error.unknown'
          )
        );
      }
    }
  };

  return (
    <div className="mt-3">
      <label htmlFor="job-preview-page" className="form-label">
        {t('job.new.pdf.preview')}
      </label>
      <div className="d-flex gap-2 mb-2">
        <input
          id="job-preview-page"
          type="number"
          min={1}
          max={material.pdfPageCount}
          step={1}
          className="form-control w-auto"
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={isFetching}
          onClick={() => void show()}
        >
          {t('job.new.pdf.previewShow')}
        </button>
      </div>
      {error && <div className="invalid-feedback d-block">{error}</div>}
      {shown &&
        (shown.text.trim() === '' ? (
          <p className="qz-small qz-muted">{t('job.new.pdf.previewEmpty')}</p>
        ) : (
          <div
            className="border rounded p-2 overflow-auto"
            style={{ maxHeight: '20rem', whiteSpace: 'pre-wrap' }}
          >
            {shown.text}
          </div>
        ))}
    </div>
  );
};
